import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, alpha } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface DataChartProps {
  title: string;
  subtitle?: string;
  type: 'line' | 'area' | 'bar' | 'donut';
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  height?: number;
  colors?: string[];
  xaxis?: {
    categories?: string[];
  };
  labels?: string[];
}

const DataChart: React.FC<DataChartProps> = ({
  title,
  subtitle,
  type,
  series,
  height = 350,
  colors,
  xaxis,
  labels
}) => {
  const theme = useTheme();
  
  const getDefaultColors = () => {
    if (colors) return colors;
    
    return [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ];
  };
  
  const getOptions = (): ApexOptions => {
    const defaultOptions: ApexOptions = {
      chart: {
        type,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: type !== 'donut',
          type: 'x',
        },
        fontFamily: theme.typography.fontFamily,
        background: 'transparent',
        animations: {
          enabled: true,
          speed: 800,
        },
      },
      colors: getDefaultColors(),
      grid: {
        borderColor: alpha(theme.palette.divider, 0.5),
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: type !== 'donut',
          },
        },
        yaxis: {
          lines: {
            show: type !== 'donut',
          },
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      dataLabels: {
        enabled: type === 'donut',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#fff'],
        },
        dropShadow: {
          enabled: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: type === 'area' ? 3 : 2,
        lineCap: 'round',
      },
      xaxis: {
        categories: xaxis?.categories || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        labels: {
          style: {
            colors: theme.palette.text.secondary,
            fontSize: '12px',
            fontWeight: 500,
          },
          rotateAlways: type === 'bar' && xaxis?.categories && xaxis.categories.length > 6,
          hideOverlappingLabels: true,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme.palette.text.secondary,
            fontSize: '12px',
            fontWeight: 500,
          },
          formatter: (val) => {
            return val.toFixed(0);
          },
        },
      },
      tooltip: {
        theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
        style: {
          fontSize: '12px',
        },
        x: {
          format: 'dd MMM yyyy',
        },
        y: {
          formatter: (val) => {
            return val.toLocaleString();
          },
        },
        marker: {
          show: true,
        },
        fixed: {
          enabled: false,
        },
        followCursor: true,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        labels: {
          colors: theme.palette.text.primary,
          useSeriesColors: false,
        },
        markers: {
          size: 6,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: getDefaultColors().map(color => alpha(color, 0.3)),
          inverseColors: false,
          opacityFrom: type === 'area' ? 0.6 : 1,
          opacityTo: type === 'area' ? 0.1 : 1,
          stops: [0, 100],
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '60%',
          borderRadius: 6,
          borderRadiusApplication: 'end',
          dataLabels: {
            position: 'top',
          },
        },
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                fontWeight: 600,
                color: theme.palette.text.primary,
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 700,
                color: theme.palette.text.primary,
                offsetY: 10,
                formatter: (val) => val.toString(),
              },
              total: {
                show: true,
                showAlways: false,
                label: 'Total',
                fontSize: '14px',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                formatter: (w) => {
                  return w.globals.seriesTotals
                    .reduce((a: number, b: number) => a + b, 0)
                    .toString();
                },
              },
            },
          },
        },
      },
    };
    
    // Add donut chart specific options
    if (type === 'donut') {
      defaultOptions.legend = {
        ...defaultOptions.legend,
        position: 'bottom',
        horizontalAlign: 'center',
      };
      
      // Add labels for donut chart if provided
      if (labels) {
        defaultOptions.labels = labels;
      }
    }
    
    return defaultOptions;
  };
  
  return (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
      height: '100%',
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
        transform: 'translateY(-2px)',
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              mb: 0.5,
              color: theme.palette.text.primary
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 400
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ height, position: 'relative' }}>
          <ReactApexChart
            options={getOptions()}
            series={series}
            type={type}
            height="100%"
            width="100%"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default DataChart;
